"use client";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ManagementHeader from "../../../components/ManagementHeader";
import Footer from "../../../components/Footer";
import { supabase } from "../../../supabaseClient";
import { useFeatureFlags } from "../../../contexts/FeatureFlagsContext";
import { COMMON_NAME_PREFIXES, formatProfileName } from "../../../utils/profileName";
import {
  FaChevronDown,
  FaExclamationTriangle,
  FaIdBadge,
  FaKey,
  FaPlus,
  FaSearch,
  FaShieldAlt,
  FaSignOutAlt,
  FaTimes,
  FaTrash,
  FaBan,
  FaUnlock,
  FaUser,
  FaUserCog,
  FaUserPlus,
  FaUsers,
  FaHospitalUser,
} from "react-icons/fa";

const SUPER_MANAGEMENT_EMAILS = (
  process.env.REACT_APP_SUPER_MANAGEMENT_EMAILS || ""
)
  .split(",")
  .map((e) => e.trim().toLowerCase())
  .filter(Boolean);

const PERMISSION_LABELS = {
  management_portal: "Management portal",
  recruitment_applications: "Recruitment applications",
  shift_management: "Shift management",
  datix_reports: "DATIX reports",
  leave_requests: "Leave requests",
};

export default function Profiles() {
  const navigate = useNavigate();
  const { isEnabled } = useFeatureFlags();
  const [profiles, setProfiles] = useState([]);
  const [patients, setPatients] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [currentUserEmail, setCurrentUserEmail] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [disciplinaryCounts, setDisciplinaryCounts] = useState({});
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [accountActionId, setAccountActionId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [suspensionOpen, setSuspensionOpen] = useState(null);
  const [suspending, setSuspending] = useState(false);
  const [suspensionError, setSuspensionError] = useState("");
  const [suspensionHistory, setSuspensionHistory] = useState({});
  const [suspensionForm, setSuspensionForm] = useState({ reason: "", duration: "indefinite", customUntil: "" });
  const [createForm, setCreateForm] = useState({
    email: "",
    temporaryPassword: "",
    fullName: "",
    namePrefix: "",
    robloxName: "",
    robloxUserId: "",
    discordName: "",
    discordId: "",
    jobRole: "",
    accountType: "Staff",
  });

  useEffect(() => {
    fetchProfiles();
    fetchPatients();
    fetchCurrentUser();
  }, []);

  const fetchProfiles = async () => {
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .order("created_at", { ascending: true });

    if (error) {
      console.error("Error fetching profiles:", error);
      return;
    }

    const rows = data || [];
    setProfiles(rows);

    const { data: suspensionRows, error: suspensionError } = await supabase
      .from("staff_suspensions")
      .select("*")
      .order("created_at", { ascending: false });

    if (!suspensionError) {
      const grouped = {};
      (suspensionRows || []).forEach((row) => {
        if (!grouped[row.profile_id]) grouped[row.profile_id] = [];
        grouped[row.profile_id].push(row);
      });
      setSuspensionHistory(grouped);
    } else {
      console.warn("Unable to load suspension history:", suspensionError);
    }

    if (rows.length) {
      const counts = {};
      await Promise.all(
        rows.map(async (profile) => {
          const { data: discData } = await supabase
            .from("disciplinaries")
            .select("id")
            .eq("profile_id", profile.id);
          counts[profile.id] = discData?.length || 0;
        }),
      );
      setDisciplinaryCounts(counts);
    }
  };


  const fetchPatients = async () => {
    const { data, error } = await supabase
      .from("patient_portal_accounts")
      .select("id, roleplay_name, roleplay_nhs_number, roblox_username, roblox_display_name, discord_username, discord_id, date_of_birth, created_at")
      .order("created_at", { ascending: true });

    if (error) {
      console.error("Error fetching patient portal accounts:", error);
      setPatients([]);
      return;
    }

    setPatients(data || []);
  };

  const fetchCurrentUser = async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (session?.user) {
      setCurrentUserId(session.user.id);
      if (session.user.email) setCurrentUserEmail(session.user.email.toLowerCase());
    }
  };

  const handlePositionChange = async (id, newPosition) => {
    const existingProfile = profiles.find((p) => p.id === id);
    if (!existingProfile || existingProfile.position === newPosition) return;

    const previousPosition = existingProfile.position || "Visitor";
    const previousManagementPortal = existingProfile.management_portal || false;
    const nextManagementPortal =
      newPosition === "Management"
        ? true
        : existingProfile.management_portal || false;

    setProfiles((prev) =>
      prev.map((p) =>
        p.id === id
          ? {
              ...p,
              position: newPosition,
              management_portal: nextManagementPortal,
            }
          : p,
      ),
    );
    setSavingId(id);

    const { error } = await supabase
      .from("profiles")
      .update({
        position: newPosition,
        ...(newPosition === "Management" ? { management_portal: true } : {}),
      })
      .eq("id", id);

    if (error) {
      console.error("Failed to update account position:", error);
      setProfiles((prev) =>
        prev.map((p) =>
          p.id === id
            ? {
                ...p,
                position: previousPosition,
                management_portal: previousManagementPortal,
              }
            : p,
        ),
      );
      alert("Failed to update the account position. No changes were saved.");
    }

    setSavingId(null);
  };

  const handleScopeChange = (id, scope, value) => {
    setProfiles((prev) =>
      prev.map((p) => (p.id === id ? { ...p, [scope]: value } : p)),
    );
  };

  const saveProfile = async (profile) => {
    setSavingId(profile.id);

    const isManagementTarget = String(profile.position || "").toLowerCase() === "management";
    const updatePayload = isManagementTarget
      ? {
          name_prefix: String(profile.name_prefix || "").trim() || null,
          job_role: String(profile.job_role || "").trim() || null,
        }
      : {
          position: profile.position,
          name_prefix: String(profile.name_prefix || "").trim() || null,
          job_role: String(profile.job_role || "").trim() || null,
          management_portal: profile.management_portal,
          recruitment_applications: profile.recruitment_applications,
          shift_management: profile.shift_management,
          datix_reports: profile.datix_reports,
          leave_requests: profile.leave_requests,
        };

    const { error } = await supabase
      .from("profiles")
      .update(updatePayload)
      .eq("id", profile.id);

    if (error) alert(error.message || "Failed to save changes");
    setSavingId(null);
  };

  const accountAdminRequest = async (body) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error("Your management session has expired. Sign in again.");

    const response = await fetch("/api/account-admin", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify(body),
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(result.error || "The account action failed");
    return result;
  };

  const forceLogout = async (profile) => {
    if (!window.confirm(`Force ${formatProfileName(profile, profile.email || "this account")} to sign out of PGUH on all active tabs/devices?`)) return;
    setAccountActionId(profile.id);
    try {
      const result = await accountAdminRequest({ action: "force_logout", userId: profile.id });
      setProfiles((current) => current.map((row) => row.id === profile.id ? { ...row, force_logout_at: result.force_logout_at } : row));
      alert(`${formatProfileName(profile, profile.email || "this account")} has been marked for immediate forced logout.`);
    } catch (error) {
      alert(error.message || "Unable to force logout this account.");
    } finally {
      setAccountActionId(null);
    }
  };

  const deleteAccount = async (profile) => {
    const identity = profile.email || profile.full_name || profile.id;
    const typed = window.prompt(
      `This permanently deletes the login account.\n\nType DELETE ${identity} to confirm.`,
    );
    if (typed !== `DELETE ${identity}`) return;

    setAccountActionId(profile.id);
    try {
      await accountAdminRequest({ action: "delete", userId: profile.id });
      setProfiles((current) => current.filter((row) => row.id !== profile.id));
      setOpenId((current) => current === profile.id ? null : current);
      alert(`${identity} has been deleted.`);
    } catch (error) {
      alert(error.message || "Unable to delete this account.");
    } finally {
      setAccountActionId(null);
    }
  };

  const openSuspension = (profile) => {
    setSuspensionError("");
    setSuspensionForm({ reason: "", duration: "indefinite", customUntil: "" });
    setSuspensionOpen(profile);
  };

  const calculateSuspendedUntil = () => {
    if (suspensionForm.duration === "indefinite") return null;
    if (suspensionForm.duration === "custom") {
      if (!suspensionForm.customUntil) return null;
      const parsed = new Date(suspensionForm.customUntil);
      if (!Number.isFinite(parsed.getTime()) || parsed.getTime() <= Date.now()) return null;
      return parsed.toISOString();
    }

    const durationHours = {
      "1h": 1,
      "24h": 24,
      "3d": 72,
      "7d": 168,
      "30d": 720,
    }[suspensionForm.duration];
    return durationHours ? new Date(Date.now() + durationHours * 60 * 60 * 1000).toISOString() : null;
  };

  const suspendAccount = async (event) => {
    event.preventDefault();
    if (!suspensionOpen) return;
    const reason = suspensionForm.reason.trim();
    if (!reason) {
      setSuspensionError("Enter a reason for the suspension.");
      return;
    }
    if (suspensionForm.duration === "custom" && !calculateSuspendedUntil()) {
      setSuspensionError("Choose a future date and time for the suspension to end.");
      return;
    }

    setSuspending(true);
    setSuspensionError("");
    try {
      const result = await accountAdminRequest({
        action: "suspend",
        userId: suspensionOpen.id,
        reason,
        suspendedUntil: calculateSuspendedUntil(),
      });
      setProfiles((current) => current.map((row) => row.id === suspensionOpen.id ? { ...row, ...result.profile } : row));
      if (result.suspension) {
        setSuspensionHistory((current) => ({
          ...current,
          [suspensionOpen.id]: [result.suspension, ...(current[suspensionOpen.id] || [])],
        }));
      }
      setSuspensionOpen(null);
      alert(`${formatProfileName(suspensionOpen, suspensionOpen.email || "this account")} has been suspended and signed out.`);
    } catch (error) {
      setSuspensionError(error.message || "Unable to suspend this account.");
    } finally {
      setSuspending(false);
    }
  };

  const liftSuspension = async (profile) => {
    const reason = window.prompt(`Reason for lifting ${formatProfileName(profile, profile.email || "this account")}'s suspension (optional):`, "Management review");
    if (reason === null) return;
    setAccountActionId(profile.id);
    try {
      const result = await accountAdminRequest({ action: "unsuspend", userId: profile.id, reason: reason.trim() || null });
      setProfiles((current) => current.map((row) => row.id === profile.id ? { ...row, ...result.profile } : row));
      if (result.suspension) {
        setSuspensionHistory((current) => ({
          ...current,
          [profile.id]: (current[profile.id] || []).map((item) => item.id === result.suspension.id ? result.suspension : item),
        }));
      }
      alert(`${formatProfileName(profile, profile.email || "this account")}'s suspension has been lifted.`);
    } catch (error) {
      alert(error.message || "Unable to lift this suspension.");
    } finally {
      setAccountActionId(null);
    }
  };

  const updateCreateField = (field, value) => {
    setCreateForm((current) => ({ ...current, [field]: value }));
  };

  const createAccount = async (event) => {
    event.preventDefault();
    setCreateError("");
    setCreating(true);
    try {
      const result = await accountAdminRequest({ action: "create", ...createForm });
      setProfiles((current) => [...current, result.profile].sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0)));
      setCreateOpen(false);
      setCreateForm({
        email: "", temporaryPassword: "", fullName: "", namePrefix: "", robloxName: "",
        robloxUserId: "", discordName: "", discordId: "", jobRole: "", accountType: "Staff",
      });
      alert(`Account created for ${result.profile?.email || createForm.email}. Their email is already confirmed and they must change the temporary password at first login.`);
    } catch (error) {
      setCreateError(error.message || "Unable to create the account.");
    } finally {
      setCreating(false);
    }
  };

  const formatDate = (dt) => {
    if (!dt) return "Not available";
    const d = new Date(dt);
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(d);
  };

  const counts = useMemo(() => {
    const management = profiles.filter((p) => p.position === "Management").length;
    const staff = profiles.filter((p) => p.position === "Staff").length;
    const visitors = profiles.filter(
      (p) => !p.position || p.position === "Visitor",
    ).length;
    const patientCount = patients.length;
    return { all: profiles.length + patientCount, management, staff, visitors, patients: patientCount };
  }, [patients, profiles]);

  const filteredProfiles = useMemo(() => {
    const term = search.trim().toLowerCase();

    if (filter === "patients") return [];

    return profiles.filter((profile) => {
      const position = profile.position || "Visitor";
      const matchesFilter =
        filter === "all" || position.toLowerCase() === filter.toLowerCase();

      if (!matchesFilter) return false;
      if (!term) return true;

      return [
        profile.name_prefix,
        profile.full_name,
        formatProfileName(profile, ""),
        profile.email,
        profile.roblox_name,
        profile.discord_name,
        profile.job_role,
        position,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term));
    });
  }, [filter, profiles, search]);

  const filteredPatients = useMemo(() => {
    if (filter !== "all" && filter !== "patients") return [];
    const term = search.trim().toLowerCase();
    return patients.filter((patient) => {
      if (!term) return true;
      return [
        patient.roleplay_name,
        patient.roleplay_nhs_number,
        patient.roblox_username,
        patient.roblox_display_name,
        patient.discord_username,
        patient.discord_id,
        "Patient",
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term));
    });
  }, [filter, patients, search]);

  const positionIcon = (position) => {
    if (position === "Management") return <FaShieldAlt aria-hidden="true" />;
    if (position === "Staff") return <FaIdBadge aria-hidden="true" />;
    return <FaUser aria-hidden="true" />;
  };

  const renderPatient = (patient) => (
    <article key={`patient-${patient.id}`} className="pguh-management-profile-row pguh-management-profile-row--patient">
      <button
        type="button"
        className="pguh-management-profile-row__summary"
        onClick={() => navigate(`/intranet/ecamis/patient-overview/${patient.id}`)}
      >
        <span className="pguh-management-profile-avatar pguh-management-profile-avatar--patient">
          <FaHospitalUser aria-hidden="true" />
        </span>
        <span className="pguh-management-profile-row__identity">
          <strong>{patient.roleplay_name || patient.roblox_display_name || patient.roblox_username || "Patient"}</strong>
          <span>
            Patient portal account
            {patient.roleplay_nhs_number ? ` · NHS: ${patient.roleplay_nhs_number}` : ""}
            {patient.roblox_username ? ` · Roblox: ${patient.roblox_username}` : ""}
          </span>
        </span>
        <span className="pguh-management-profile-row__metadata">
          <span className="pguh-management-profile-badge pguh-management-profile-badge--patient">Patient</span>
        </span>
        <span className="pguh-management-profile-row__chevron" aria-hidden="true">›</span>
      </button>
    </article>
  );

  const renderProfile = (profile) => {
    const position = profile.position || "Visitor";
    const isManagement = position === "Management";
    const isOpen = openId === profile.id;
    const targetEmail = String(profile.email || "").toLowerCase();
    const editable =
      !isManagement ||
      (SUPER_MANAGEMENT_EMAILS.includes(currentUserEmail) &&
        !SUPER_MANAGEMENT_EMAILS.includes(targetEmail));
    const canEditManagementRolePrefix =
      !isManagement || (Boolean(currentUserId) && currentUserId !== profile.id);
    const canEditPermissions = !isManagement && editable;
    const disciplinaryCount = disciplinaryCounts[profile.id] || 0;
    const canSuspend = currentUserId && currentUserId !== profile.id;
    const history = suspensionHistory[profile.id] || [];

    return (
      <article
        key={profile.id}
        className={`pguh-management-profile-row${isOpen ? " pguh-management-profile-row--open" : ""}`}
      >
        <button
          type="button"
          className="pguh-management-profile-row__summary"
          onClick={() => setOpenId(isOpen ? null : profile.id)}
          aria-expanded={isOpen}
        >
          <span className={`pguh-management-profile-avatar pguh-management-profile-avatar--${position.toLowerCase()}`}>
            {positionIcon(position)}
          </span>

          <span className="pguh-management-profile-row__identity">
            <strong>{formatProfileName(profile, "Unnamed account")}</strong>
            <span>
              {profile.job_role || position}
              {profile.roblox_name ? ` · Roblox: ${profile.roblox_name}` : ""}
            </span>
          </span>

          <span className="pguh-management-profile-row__metadata">
            <span className={`pguh-management-profile-badge pguh-management-profile-badge--${position.toLowerCase()}`}>
              {position}
            </span>
            {profile.suspended && (
              <span className="pguh-management-profile-badge pguh-management-profile-badge--warning">
                Suspended
              </span>
            )}
            {profile.banned && (
              <span className="pguh-management-profile-badge pguh-management-profile-badge--danger">
                Banned
              </span>
            )}
            {profile.must_change_password && (
              <span className="pguh-management-profile-badge pguh-management-profile-badge--temporary">
                Temporary password
              </span>
            )}
          </span>

          <FaChevronDown
            aria-hidden="true"
            className={`pguh-management-profile-row__chevron${isOpen ? " is-open" : ""}`}
          />
        </button>

        {isOpen && (
          <div className="pguh-management-profile-row__details">
            <div className="pguh-management-profile-detail-grid">
              <div>
                <span>Email address</span>
                <strong>{profile.email || "Not set"}</strong>
              </div>
              <div>
                <span>Roblox username</span>
                <strong>{profile.roblox_name || "Not set"}</strong>
              </div>
              <div>
                <span>Discord</span>
                <strong>{profile.discord_name || "Not linked"}{profile.discord_id ? ` · ${profile.discord_id}` : ""}</strong>
              </div>
              <div>
                <span>Roblox user ID</span>
                <strong>{profile.roblox_user_id || "Not set"}</strong>
              </div>
              <div>
                <span>Account created</span>
                <strong>{formatDate(profile.created_at)}</strong>
              </div>
              <div>
                <span>Last forced logout</span>
                <strong>{profile.force_logout_at ? formatDate(profile.force_logout_at) : "Never"}</strong>
              </div>
            </div>

            {profile.suspended && (
              <section className="pguh-management-profile-suspension-summary" aria-label="Current suspension">
                <div className="pguh-management-profile-suspension-summary__icon"><FaBan aria-hidden="true" /></div>
                <div>
                  <strong>Account currently suspended</strong>
                  <p>{profile.suspension_reason || "No suspension reason recorded."}</p>
                  <small>
                    Suspended {profile.suspended_at ? formatDate(profile.suspended_at) : "date unavailable"}
                    {profile.suspended_until ? ` · Ends ${formatDate(profile.suspended_until)}` : " · Indefinite"}
                  </small>
                </div>
              </section>
            )}

            {history.length > 0 && (
              <details className="pguh-management-profile-suspension-history">
                <summary>Suspension history ({history.length})</summary>
                <div className="pguh-management-profile-suspension-history__list">
                  {history.map((item) => (
                    <div key={item.id}>
                      <strong>{item.lifted_at ? "Lifted suspension" : "Suspension"}</strong>
                      <span>{item.reason || "No reason recorded"}</span>
                      <small>
                        Started {formatDate(item.started_at || item.created_at)}
                        {item.ends_at ? ` · Scheduled end ${formatDate(item.ends_at)}` : " · Indefinite"}
                        {item.lifted_at ? ` · Lifted ${formatDate(item.lifted_at)}` : ""}
                      </small>
                    </div>
                  ))}
                </div>
              </details>
            )}

            <div className="pguh-management-profile-editor-grid">
              <section className="pguh-management-profile-panel">
                <div className="pguh-management-profile-panel__heading">
                  <FaUserCog aria-hidden="true" />
                  <div>
                    <h3>Account and role</h3>
                    <p>{isManagement ? "Management account type is protected. You can update another manager's title and name prefix." : "Manage this user's position and staff specialty."}</p>
                  </div>
                </div>

                <div className="nhsuk-form-group">
                  <label className="nhsuk-label nhsuk-label--s" htmlFor={`position-${profile.id}`}>
                    Position
                  </label>
                  {isManagement && !editable ? (
                    <div className="pguh-management-profile-locked">
                      This management account is protected.
                    </div>
                  ) : (
                    <select
                      id={`position-${profile.id}`}
                      className="nhsuk-select"
                      value={position}
                      onChange={(e) => handlePositionChange(profile.id, e.target.value)}
                      disabled={!editable || savingId === profile.id}
                    >
                      <option value="Visitor">Visitor</option>
                      <option value="Staff">Staff</option>
                      <option value="Management">Management</option>
                    </select>
                  )}
                </div>

                <div className="nhsuk-form-group">
                  <label className="nhsuk-label nhsuk-label--s" htmlFor={`name-prefix-${profile.id}`}>
                    Name prefix / title
                  </label>
                  <input
                    id={`name-prefix-${profile.id}`}
                    className="nhsuk-input nhsuk-input--width-10"
                    type="text"
                    list={`name-prefix-options-${profile.id}`}
                    placeholder="e.g. Dr."
                    value={profile.name_prefix || ""}
                    onChange={(e) => handleScopeChange(profile.id, "name_prefix", e.target.value)}
                    disabled={!canEditManagementRolePrefix || savingId === profile.id}
                    maxLength={32}
                  />
                  <datalist id={`name-prefix-options-${profile.id}`}>
                    {COMMON_NAME_PREFIXES.map((prefix) => <option key={prefix} value={prefix} />)}
                  </datalist>
                  <div className="nhsuk-hint">
                    Displayed before this person's name throughout RUHT, for example Dr. Alex Smith. Leave blank for no prefix.
                  </div>
                </div>

                <div className="nhsuk-form-group">
                  <label className="nhsuk-label nhsuk-label--s" htmlFor={`job-role-${profile.id}`}>
                    Job role / specialty
                  </label>
                  <input
                    id={`job-role-${profile.id}`}
                    className="nhsuk-input"
                    type="text"
                    placeholder="e.g. Emergency Nurse, Consultant, HCSW"
                    value={profile.job_role || ""}
                    onChange={(e) => handleScopeChange(profile.id, "job_role", e.target.value)}
                    disabled={!canEditManagementRolePrefix || savingId === profile.id}
                  />
                  <div className="nhsuk-hint">
                    Used for role-based E-Learning and staff management.
                  </div>
                </div>
              </section>

              <section className="pguh-management-profile-panel">
                <div className="pguh-management-profile-panel__heading">
                  <FaShieldAlt aria-hidden="true" />
                  <div>
                    <h3>Management permissions</h3>
                    <p>Choose the management functions this account can access.</p>
                  </div>
                </div>

                {canEditPermissions ? (
                  <div className="pguh-management-profile-permissions">
                    {Object.entries(PERMISSION_LABELS).map(([scope, label]) => (
                      <label key={scope}>
                        <input
                          type="checkbox"
                          checked={Boolean(profile[scope])}
                          onChange={(e) => handleScopeChange(profile.id, scope, e.target.checked)}
                        />
                        <span>
                          <strong>{label}</strong>
                          <small>{profile[scope] ? "Access enabled" : "No access"}</small>
                        </span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="pguh-management-profile-locked">
                    {isManagement
                      ? "Management account permissions are protected and cannot be changed from Profiles."
                      : "Permissions for this account cannot be changed by your account."}
                  </div>
                )}
              </section>
            </div>

            <div className="pguh-management-profile-row__actions">
              <div className="pguh-management-profile-row__actions-left">
                <button
                  type="button"
                  className="pguh-management-profile-disciplinary"
                  onClick={() => navigate(`/intranet/disciplinaries/${profile.id}`)}
                >
                  <FaExclamationTriangle aria-hidden="true" />
                  {disciplinaryCount === 0
                    ? "No disciplinaries · View record"
                    : `${disciplinaryCount} ${disciplinaryCount === 1 ? "disciplinary" : "disciplinaries"} · View record`}
                </button>
              </div>

              <div className="pguh-management-profile-row__actions-right">
                {canSuspend && isEnabled("feature.management_suspensions") && (profile.suspended ? (
                  <button
                    type="button"
                    className="nhsuk-button pguh-account-action-button pguh-account-action-button--unsuspend"
                    onClick={() => liftSuspension(profile)}
                    disabled={accountActionId === profile.id}
                  >
                    <FaUnlock aria-hidden="true" />
                    Lift suspension
                  </button>
                ) : (
                  <button
                    type="button"
                    className="nhsuk-button nhsuk-button--warning pguh-account-action-button pguh-account-action-button--suspend"
                    onClick={() => openSuspension(profile)}
                    disabled={accountActionId === profile.id}
                  >
                    <FaBan aria-hidden="true" />
                    Suspend account
                  </button>
                ))}

                {isEnabled("feature.management_force_logout") && <button
                  type="button"
                  className="nhsuk-button nhsuk-button--warning pguh-account-action-button"
                  onClick={() => forceLogout(profile)}
                  disabled={accountActionId === profile.id}
                >
                  <FaSignOutAlt aria-hidden="true" />
                  Force logout
                </button>}

                {editable && isEnabled("feature.management_account_deletion") && (
                  <button
                    type="button"
                    className="nhsuk-button nhsuk-button--secondary pguh-account-action-button pguh-account-action-button--danger"
                    onClick={() => deleteAccount(profile)}
                    disabled={accountActionId === profile.id}
                  >
                    <FaTrash aria-hidden="true" />
                    Delete account
                  </button>
                )}

                {(editable || canEditManagementRolePrefix) && (
                  <button
                    type="button"
                    className="nhsuk-button"
                    disabled={savingId === profile.id || (!isManagement && !isEnabled("feature.management_permission_editing"))}
                    onClick={() => saveProfile(profile)}
                  >
                    {savingId === profile.id ? "Saving..." : "Save changes"}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </article>
    );
  };

  return (
    <div className="flex flex-col min-h-screen">
      <ManagementHeader />

      {suspensionOpen && (
        <div className="pguh-suspension-modal-overlay" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !suspending) setSuspensionOpen(null); }}>
          <section className="pguh-suspension-modal" role="dialog" aria-modal="true" aria-labelledby="suspension-modal-title">
            <header className="pguh-suspension-modal__header">
              <div>
                <span>Account administration</span>
                <h2 id="suspension-modal-title">Suspend {formatProfileName(suspensionOpen, suspensionOpen.email || "this account")}</h2>
              </div>
              <button type="button" aria-label="Close suspension dialog" onClick={() => setSuspensionOpen(null)} disabled={suspending}><FaTimes /></button>
            </header>
            <form onSubmit={suspendAccount}>
              <div className="pguh-suspension-modal__body">
                <div className="nhsuk-warning-callout">
                  <h3 className="nhsuk-warning-callout__label"><span role="text"><span className="nhsuk-u-visually-hidden">Important: </span>Immediate access removal</span></h3>
                  <p>Suspending this account will sign the user out of all active PGUH sessions. Management accounts can also be suspended.</p>
                </div>
                {suspensionError && <div className="nhsuk-error-summary" role="alert"><div className="nhsuk-error-summary__body"><p>{suspensionError}</p></div></div>}
                <div className="nhsuk-form-group">
                  <label className="nhsuk-label nhsuk-label--s" htmlFor="suspension-reason">Reason for suspension</label>
                  <textarea id="suspension-reason" className="nhsuk-textarea" rows="5" required value={suspensionForm.reason} onChange={(e) => setSuspensionForm((current) => ({ ...current, reason: e.target.value }))} placeholder="Explain why this account is being suspended." />
                </div>
                <div className="nhsuk-form-group">
                  <label className="nhsuk-label nhsuk-label--s" htmlFor="suspension-duration">Suspension duration</label>
                  <select id="suspension-duration" className="nhsuk-select" value={suspensionForm.duration} onChange={(e) => setSuspensionForm((current) => ({ ...current, duration: e.target.value }))}>
                    <option value="indefinite">Indefinite — management must lift it</option>
                    <option value="1h">1 hour</option>
                    <option value="24h">24 hours</option>
                    <option value="3d">3 days</option>
                    <option value="7d">7 days</option>
                    <option value="30d">30 days</option>
                    <option value="custom">Custom end date/time</option>
                  </select>
                </div>
                {suspensionForm.duration === "custom" && (
                  <div className="nhsuk-form-group">
                    <label className="nhsuk-label nhsuk-label--s" htmlFor="suspension-until">Suspended until</label>
                    <input id="suspension-until" className="nhsuk-input" type="datetime-local" required value={suspensionForm.customUntil} onChange={(e) => setSuspensionForm((current) => ({ ...current, customUntil: e.target.value }))} />
                  </div>
                )}
              </div>
              <footer className="pguh-suspension-modal__footer">
                <button type="button" className="nhsuk-button nhsuk-button--secondary" onClick={() => setSuspensionOpen(null)} disabled={suspending}>Cancel</button>
                <button type="submit" className="nhsuk-button nhsuk-button--warning" disabled={suspending}><FaBan aria-hidden="true" /> {suspending ? "Suspending..." : "Suspend account"}</button>
              </footer>
            </form>
          </section>
        </div>
      )}

      <main className="nhsuk-width-container pguh-management-profiles-page">
        <div className="pguh-management-profiles-heading">
          <div>
            <span className="pguh-management-profiles-kicker">Management</span>
            <h1>User profiles</h1>
            <p>
              Search staff accounts, review account information and manage roles, access and authentication.
            </p>
          </div>
          {isEnabled("feature.management_account_creation") && (
            <button type="button" className="nhsuk-button pguh-management-create-account" onClick={() => { setCreateError(""); setCreateOpen(true); }}>
              <FaUserPlus aria-hidden="true" /> Create account
            </button>
          )}
        </div>

        {createOpen && isEnabled("feature.management_account_creation") && (
          <div className="pguh-account-modal-overlay" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget && !creating) setCreateOpen(false); }}>
            <section className="pguh-account-modal" role="dialog" aria-modal="true" aria-labelledby="create-account-title">
              <div className="pguh-account-modal__header">
                <div>
                  <span className="pguh-management-profiles-kicker">Account administration</span>
                  <h2 id="create-account-title">Create PGUH account</h2>
                  <p>Create an already-confirmed login with a one-time temporary password.</p>
                </div>
                <button type="button" onClick={() => setCreateOpen(false)} disabled={creating} aria-label="Close create account"><FaTimes /></button>
              </div>

              <form onSubmit={createAccount} className="pguh-account-modal__form">
                <div className="pguh-account-modal__body">
                  {createError && <div className="nhsuk-error-summary" role="alert"><p>{createError}</p></div>}

                  <div className="pguh-account-form-section">
                    <div className="pguh-account-form-section__heading"><FaUser aria-hidden="true" /><div><h3>Account identity</h3><p>Core details used to identify this person across PGUH services.</p></div></div>
                    <div className="pguh-account-form-grid">
                      <div className="nhsuk-form-group">
                        <label className="nhsuk-label nhsuk-label--s" htmlFor="new-account-prefix">Name prefix / title</label>
                        <input id="new-account-prefix" className="nhsuk-input" list="new-account-prefix-options" placeholder="e.g. Dr." maxLength={32} value={createForm.namePrefix} onChange={(e) => updateCreateField("namePrefix", e.target.value)} />
                        <datalist id="new-account-prefix-options">{COMMON_NAME_PREFIXES.map((prefix) => <option key={prefix} value={prefix} />)}</datalist>
                      </div>
                      <div className="nhsuk-form-group">
                        <label className="nhsuk-label nhsuk-label--s" htmlFor="new-account-name">Full name</label>
                        <input id="new-account-name" className="nhsuk-input" value={createForm.fullName} onChange={(e) => updateCreateField("fullName", e.target.value)} required />
                      </div>
                      <div className="nhsuk-form-group">
                        <label className="nhsuk-label nhsuk-label--s" htmlFor="new-account-email">Email</label>
                        <input id="new-account-email" className="nhsuk-input" type="email" value={createForm.email} onChange={(e) => updateCreateField("email", e.target.value)} required />
                        <div className="nhsuk-hint">Management-created accounts are confirmed automatically.</div>
                      </div>
                      <div className="nhsuk-form-group">
                        <label className="nhsuk-label nhsuk-label--s" htmlFor="new-account-role">Role / position</label>
                        <input id="new-account-role" className="nhsuk-input" placeholder="e.g. Emergency Nurse, Consultant" value={createForm.jobRole} onChange={(e) => updateCreateField("jobRole", e.target.value)} />
                      </div>
                      <div className="nhsuk-form-group">
                        <label className="nhsuk-label nhsuk-label--s" htmlFor="new-account-type">Account type</label>
                        <select id="new-account-type" className="nhsuk-select" value={createForm.accountType} onChange={(e) => updateCreateField("accountType", e.target.value)}>
                          <option value="Visitor">Visitor</option>
                          <option value="Staff">Staff</option>
                          <option value="Management">Management</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="pguh-account-form-section">
                    <div className="pguh-account-form-section__heading"><FaIdBadge aria-hidden="true" /><div><h3>Linked identities</h3><p>Set usernames and IDs directly; these do not need to be linked by the user later.</p></div></div>
                    <div className="pguh-account-form-grid">
                      <div className="nhsuk-form-group"><label className="nhsuk-label nhsuk-label--s" htmlFor="new-roblox-name">Roblox username</label><input id="new-roblox-name" className="nhsuk-input" value={createForm.robloxName} onChange={(e) => updateCreateField("robloxName", e.target.value)} /></div>
                      <div className="nhsuk-form-group"><label className="nhsuk-label nhsuk-label--s" htmlFor="new-roblox-id">Roblox user ID</label><input id="new-roblox-id" className="nhsuk-input" inputMode="numeric" value={createForm.robloxUserId} onChange={(e) => updateCreateField("robloxUserId", e.target.value.replace(/\D/g, ""))} /></div>
                      <div className="nhsuk-form-group"><label className="nhsuk-label nhsuk-label--s" htmlFor="new-discord-name">Discord username</label><input id="new-discord-name" className="nhsuk-input" value={createForm.discordName} onChange={(e) => updateCreateField("discordName", e.target.value)} /></div>
                      <div className="nhsuk-form-group"><label className="nhsuk-label nhsuk-label--s" htmlFor="new-discord-id">Discord user ID</label><input id="new-discord-id" className="nhsuk-input" inputMode="numeric" value={createForm.discordId} onChange={(e) => updateCreateField("discordId", e.target.value.replace(/\D/g, ""))} /></div>
                    </div>
                  </div>

                  <div className="pguh-account-form-section pguh-account-form-section--security">
                    <div className="pguh-account-form-section__heading"><FaKey aria-hidden="true" /><div><h3>Temporary password</h3><p>The password is sent only to Supabase Auth and is not stored in the profile database.</p></div></div>
                    <div className="nhsuk-form-group">
                      <label className="nhsuk-label nhsuk-label--s" htmlFor="new-temp-password">Temporary password</label>
                      <input id="new-temp-password" className="nhsuk-input" type="text" autoComplete="off" minLength="8" value={createForm.temporaryPassword} onChange={(e) => updateCreateField("temporaryPassword", e.target.value)} required />
                      <div className="nhsuk-hint">At least 8 characters. The user will be forced to replace this immediately after their first login.</div>
                    </div>
                  </div>
                </div>

                <div className="pguh-account-modal__footer">
                  <button type="button" className="nhsuk-button nhsuk-button--secondary" onClick={() => setCreateOpen(false)} disabled={creating}>Cancel</button>
                  <button type="submit" className="nhsuk-button" disabled={creating}><FaPlus aria-hidden="true" /> {creating ? "Creating account..." : "Create account"}</button>
                </div>
              </form>
            </section>
          </div>
        )}

        <section className="pguh-management-profile-stats" aria-label="Profile totals">
          <div>
            <span className="pguh-management-profile-stat-icon"><FaUsers aria-hidden="true" /></span>
            <span><strong>{counts.all}</strong><small>Total accounts</small></span>
          </div>
          <div>
            <span className="pguh-management-profile-stat-icon"><FaShieldAlt aria-hidden="true" /></span>
            <span><strong>{counts.management}</strong><small>Management</small></span>
          </div>
          <div>
            <span className="pguh-management-profile-stat-icon"><FaIdBadge aria-hidden="true" /></span>
            <span><strong>{counts.staff}</strong><small>Staff</small></span>
          </div>
          <div>
            <span className="pguh-management-profile-stat-icon"><FaUser aria-hidden="true" /></span>
            <span><strong>{counts.visitors}</strong><small>Visitors</small></span>
          </div>
          <div>
            <span className="pguh-management-profile-stat-icon"><FaHospitalUser aria-hidden="true" /></span>
            <span><strong>{counts.patients}</strong><small>Patients</small></span>
          </div>
        </section>

        <section className="pguh-management-profile-directory">
          <div className="pguh-management-profile-directory__toolbar">
            <div className="pguh-management-profile-search">
              <FaSearch aria-hidden="true" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, email, Roblox username, role or NHS number"
                aria-label="Search profiles"
              />
            </div>

            <div className="pguh-management-profile-filters" aria-label="Filter profiles">
              {[
                ["all", "All", counts.all],
                ["management", "Management", counts.management],
                ["staff", "Staff", counts.staff],
                ["visitor", "Visitors", counts.visitors],
                ["patients", "Patients", counts.patients],
              ].map(([value, label, count]) => (
                <button
                  type="button"
                  key={value}
                  className={filter === value ? "is-active" : ""}
                  onClick={() => setFilter(value)}
                >
                  {label} <span>{count}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="pguh-management-profile-directory__header" aria-hidden="true">
            <span>Account</span>
            <span>Status</span>
          </div>

          <div className="pguh-management-profile-list">
            {filteredProfiles.map(renderProfile)}
            {filteredPatients.map(renderPatient)}
            {!filteredProfiles.length && !filteredPatients.length && (
              <div className="pguh-management-profile-empty">
                <FaSearch aria-hidden="true" />
                <h2>No profiles found</h2>
                <p>Try changing the filter or search term.</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
